/**
 * Files of the command's own, one picker per slot the dry run names (`FieldHint.file_slots`): every part of a
 * repeatable item's artwork in one run. The value is `{ [slot key]: ChosenFile }`; the adapter sends each file with
 * its key. A lone slot is drawn under the field's own label; several are each labelled, with their note and their own
 * errors (`<field>.<key>`) beneath. An error on the field itself shows under the group.
 */
import {
  isChosenFile,
  type ChosenFile,
  type FieldError,
  type FileSlot,
} from "@shared-core/domain/object-ui/contract";
import type { FieldDescriptor } from "@shared-core/domain/object-ui/widgets";
import { Label } from "../../primitives/label";
import { FieldFrame, fieldId, invalidProps } from "./CommandFields";
import { PlainFileField } from "./pasteScreenshot";

export function KeyedFilesField({
  field,
  value,
  slots,
  errors,
  disabled,
  onChange,
}: {
  field: FieldDescriptor;
  value: unknown;
  /** Null until the first dry run answers: no slot is drawn yet. */
  slots: FileSlot[] | null;
  errors: FieldError[];
  disabled: boolean;
  onChange: (next: Record<string, ChosenFile> | undefined) => void;
}) {
  const id = fieldId(field.path);
  const chosen = filesOf(value);
  const own = errors.filter((e) => e.field === field.path);
  const errorsOf = (key: string) =>
    errors.filter((e) => e.field === `${field.path}.${key}`);
  const pick = (key: string, file: ChosenFile | null) => {
    const next = { ...chosen };
    if (file) next[key] = file;
    else delete next[key];
    onChange(Object.keys(next).length > 0 ? next : undefined);
  };
  if (!slots || slots.length === 0) return null;
  if (slots.length === 1) {
    const slot = slots[0]!;
    const slotErrors = [...own, ...errorsOf(slot.key)];
    const noteId = `${id}-note`;
    return (
      <FieldFrame
        id={id}
        label={field.label}
        required={field.required}
        errors={slotErrors}
        hint={
          slot.note ? (
            <p id={noteId} className="text-xs text-muted-foreground">
              {slot.note}
            </p>
          ) : undefined
        }
      >
        <PlainFileField
          id={id}
          value={chosen[slot.key] ?? null}
          disabled={disabled}
          invalid={invalidProps(id, slotErrors, slot.note ? [noteId] : [])}
          onChange={(file) => pick(slot.key, file)}
        />
      </FieldFrame>
    );
  }
  return (
    <fieldset
      className="space-y-3"
      {...(own.length > 0 ? { "aria-describedby": `${id}-error` } : {})}
    >
      <legend className="text-sm font-medium">
        {field.label}
        {!field.required && (
          <span className="ml-1 font-normal text-muted-foreground">
            (optional)
          </span>
        )}
      </legend>
      {slots.map((slot) => {
        const slotId = fieldId(`${field.path}.${slot.key}`);
        const slotErrors = errorsOf(slot.key);
        const noteId = `${slotId}-note`;
        return (
          <div key={slot.key} className="space-y-1.5">
            <Label htmlFor={slotId} className="font-normal">
              {slot.label}
            </Label>
            <PlainFileField
              id={slotId}
              value={chosen[slot.key] ?? null}
              disabled={disabled}
              invalid={invalidProps(
                slotId,
                slotErrors,
                slot.note ? [noteId] : [],
              )}
              onChange={(file) => pick(slot.key, file)}
            />
            {slot.note && (
              <p id={noteId} className="text-xs text-muted-foreground">
                {slot.note}
              </p>
            )}
            {slotErrors.length > 0 && (
              <p id={`${slotId}-error`} className="text-sm text-red-600">
                {slotErrors.map((e) => e.message).join(" ")}
              </p>
            )}
          </div>
        );
      })}
      {own.length > 0 && (
        <p id={`${id}-error`} className="text-sm text-red-600">
          {own.map((e) => e.message).join(" ")}
        </p>
      )}
    </fieldset>
  );
}

function filesOf(value: unknown): Record<string, ChosenFile> {
  if (typeof value !== "object" || value === null) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, ChosenFile] => isChosenFile(entry[1]),
    ),
  );
}
